/**
 * Invitation-code — data layer (feat #172).
 *
 * Thin typed wrapper over `apiClient` for the referral / invitation-code
 * tracking endpoints:
 *
 *   GET    /api/v1/invitation-code/codes                     — listCodes
 *   POST   /api/v1/invitation-code/codes                     — createCode
 *   PATCH  /api/v1/invitation-code/codes/:id                 — updateCode
 *   DELETE /api/v1/invitation-code/codes/:id                 — deleteCode
 *   POST   /api/v1/invitation-code/redeem                    — redeemCode
 *   GET    /api/v1/invitation-code/lookup/:code              — lookupCode
 *   GET    /api/v1/invitation-code/redemptions/:code_id      — listRedemptions
 *   GET    /api/v1/invitation-code/stats/:inviter_user_id    — inviterStats
 *
 * Pure helpers (validators, formatters, query keys) live here too so they
 * can be unit-tested without a DOM or TanStack context.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Row shapes — mirror the server response exactly.
// ---------------------------------------------------------------------------

export type InvitationScope = "affiliate" | "advertiser" | "admin";

export interface InvitationCode {
	id: string;
	tenant_id: string;
	code: string;
	inviter_user_id: string;
	scope: InvitationScope;
	max_uses: number;
	use_count: number;
	expires_at: number | null;
	enabled: number;
	created_at: number;
	updated_at: number;
}

export interface InvitationRedemption {
	id: string;
	tenant_id: string;
	code_id: string;
	invitee_identifier: string;
	redeemed_at: number;
	ip: string | null;
	ua: string | null;
	metadata: string;
}

export interface ListCodesResponse {
	codes: InvitationCode[];
}

export interface CodeResponse {
	code: InvitationCode;
}

export interface LookupResponse {
	code: InvitationCode;
	redeemable: boolean;
}

export interface ListRedemptionsResponse {
	redemptions: InvitationRedemption[];
}

export interface RedeemResponse {
	redemption: InvitationRedemption;
	code: InvitationCode;
	idempotent: boolean;
}

export interface InviterStats {
	inviter_user_id: string;
	totals: { codes: number; redemptions: number };
	per_code: Array<{
		code_id: string;
		code: string;
		scope: InvitationScope;
		enabled: number;
		use_count: number;
		max_uses: number;
		redemptions: number;
	}>;
}

export interface StatsResponse {
	stats: InviterStats;
}

// ---------------------------------------------------------------------------
// Validators — mirror server contract so the UI can short-circuit obvious
// errors before the network round-trip.
// ---------------------------------------------------------------------------

const ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
const CODE_RE = /^[A-Za-z0-9._-]{3,64}$/;
export const SCOPES: readonly InvitationScope[] = [
	"affiliate",
	"advertiser",
	"admin",
];

export function validateInviterUserId(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (!ID_RE.test(trimmed)) return null;
	return trimmed;
}

/**
 * Normalise a code: trim + upper-case. Returns null on shape failure.
 * Keep in sync with `normalizeCode` in the API route.
 */
export function normalizeCode(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const upper = raw.trim().toUpperCase();
	if (!CODE_RE.test(upper)) return null;
	return upper;
}

export function validateScope(raw: string): InvitationScope | null {
	if (typeof raw !== "string") return null;
	if ((SCOPES as readonly string[]).includes(raw)) return raw as InvitationScope;
	return null;
}

export function validateMaxUses(raw: number | string): number | null {
	const n = typeof raw === "string" ? Number(raw) : raw;
	if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
	if (n < 0 || n > 1_000_000) return null;
	return n;
}

export function validateInviteeIdentifier(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const t = raw.trim();
	if (!t || t.length > 256) return null;
	return t;
}

/**
 * Accepts a local datetime string (`YYYY-MM-DDTHH:MM`) or a unix-seconds
 * number. Returns the epoch-seconds value, or `null` for an empty input,
 * or `"invalid"` for an unparseable non-empty input.
 */
export function validateExpiresAt(
	raw: string | number | null | undefined,
): number | null | "invalid" {
	if (raw == null || raw === "") return null;
	if (typeof raw === "number") {
		if (!Number.isFinite(raw) || raw <= 0) return "invalid";
		return Math.floor(raw);
	}
	const trimmed = raw.trim();
	if (!trimmed) return null;
	const ms = Date.parse(trimmed);
	if (!Number.isFinite(ms) || ms <= 0) return "invalid";
	return Math.floor(ms / 1000);
}

// ---------------------------------------------------------------------------
// Formatters — pure, DOM-free.
// ---------------------------------------------------------------------------

export function formatUnixSec(unixSec: number | null | undefined): string {
	if (unixSec == null) return "Never";
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function formatDateOnly(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function formatUses(code: InvitationCode): string {
	if (code.max_uses <= 0) return `${code.use_count} / ∞`;
	return `${code.use_count} / ${code.max_uses}`;
}

export function statusOf(
	code: InvitationCode,
	nowSec: number = Math.floor(Date.now() / 1000),
): "disabled" | "expired" | "capped" | "active" {
	if (code.enabled !== 1) return "disabled";
	if (
		typeof code.expires_at === "number" &&
		code.expires_at > 0 &&
		code.expires_at <= nowSec
	) {
		return "expired";
	}
	if (code.max_uses > 0 && code.use_count >= code.max_uses) return "capped";
	return "active";
}

// ---------------------------------------------------------------------------
// Query keys — grouped for ergonomic invalidation.
// ---------------------------------------------------------------------------

export const invitationCodeQueryKeys = {
	all: ["invitation-code"] as const,
	codes: (inviterUserId?: string) =>
		inviterUserId
			? ([...invitationCodeQueryKeys.all, "codes", inviterUserId] as const)
			: ([...invitationCodeQueryKeys.all, "codes"] as const),
	redemptions: (codeId: string) =>
		[...invitationCodeQueryKeys.all, "redemptions", codeId] as const,
	stats: (inviterUserId: string) =>
		[...invitationCodeQueryKeys.all, "stats", inviterUserId] as const,
};

// ---------------------------------------------------------------------------
// Remote bindings — thin `apiClient` shims.
// ---------------------------------------------------------------------------

const BASE = "/invitation-code";

export interface CreateCodeArgs {
	inviter_user_id: string;
	scope: InvitationScope;
	code: string;
	max_uses?: number;
	expires_at?: number | null;
	enabled?: boolean;
}

export async function createCode(args: CreateCodeArgs): Promise<InvitationCode> {
	const payload: Record<string, unknown> = {
		inviter_user_id: args.inviter_user_id,
		scope: args.scope,
		code: args.code,
	};
	if (args.max_uses !== undefined) payload.max_uses = args.max_uses;
	if (args.expires_at !== undefined && args.expires_at !== null)
		payload.expires_at = args.expires_at;
	if (args.enabled !== undefined) payload.enabled = args.enabled;
	const res = await apiClient.post<CodeResponse>(`${BASE}/codes`, payload);
	return res.code;
}

export async function listCodes(
	inviterUserId?: string,
): Promise<InvitationCode[]> {
	const res = await apiClient.get<ListCodesResponse>(`${BASE}/codes`, {
		query: inviterUserId ? { inviter_user_id: inviterUserId } : undefined,
	});
	return res.codes;
}

export interface UpdateCodeArgs {
	id: string;
	enabled?: boolean;
	max_uses?: number;
	expires_at?: number | null;
}

export async function updateCode(args: UpdateCodeArgs): Promise<InvitationCode> {
	const payload: Record<string, unknown> = {};
	if (args.enabled !== undefined) payload.enabled = args.enabled;
	if (args.max_uses !== undefined) payload.max_uses = args.max_uses;
	if (args.expires_at !== undefined) payload.expires_at = args.expires_at;
	const res = await apiClient.patch<CodeResponse>(
		`${BASE}/codes/${encodeURIComponent(args.id)}`,
		payload,
	);
	return res.code;
}

export async function deleteCode(id: string): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/codes/${encodeURIComponent(id)}`,
	);
}

export interface RedeemCodeArgs {
	code: string;
	invitee_identifier: string;
	ip?: string | null;
	ua?: string | null;
	metadata?: Record<string, unknown> | null;
}

export async function redeemCode(args: RedeemCodeArgs): Promise<RedeemResponse> {
	const payload: Record<string, unknown> = {
		code: args.code,
		invitee_identifier: args.invitee_identifier,
	};
	if (args.ip != null) payload.ip = args.ip;
	if (args.ua != null) payload.ua = args.ua;
	if (args.metadata != null) payload.metadata = args.metadata;
	return apiClient.post<RedeemResponse>(`${BASE}/redeem`, payload);
}

export async function lookupCode(code: string): Promise<LookupResponse> {
	return apiClient.get<LookupResponse>(
		`${BASE}/lookup/${encodeURIComponent(code)}`,
	);
}

export async function listRedemptions(
	codeId: string,
	limit?: number,
): Promise<InvitationRedemption[]> {
	const res = await apiClient.get<ListRedemptionsResponse>(
		`${BASE}/redemptions/${encodeURIComponent(codeId)}`,
		{ query: limit ? { limit } : undefined },
	);
	return res.redemptions;
}

export async function inviterStats(
	inviterUserId: string,
): Promise<InviterStats> {
	const res = await apiClient.get<StatsResponse>(
		`${BASE}/stats/${encodeURIComponent(inviterUserId)}`,
	);
	return res.stats;
}
